import { Button } from '@ordr-as/umami-react/button';
import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import type { MenuItemVariantDto } from '../../../../generated-api';
import { useLocation } from '../../../context/LocationContext';
import { formatPriceWithCurrency } from '../../../utils/format';
import ItemModal from './ItemModal';
import CompositeItemModal from './CompositeItemModal';
import classNames from 'clsx';
import CustomPriceDialog from './priceDialogs/CustomPriceDialog';
import GiftCardDialog from './gift-card-dialog/GiftCardDialog';
import { useIntl } from 'react-intl';
import WeightPriceDialog from './priceDialogs/WeightPriceDialog';
import { useCartStore } from '../../../context/cart/CartStore';
import { useStandaloneOrderLineQuantity } from '../../../context/cart/useCartHelpers';
import { shallow } from 'zustand/shallow';
import type { AddComponentDto, AddItemDto } from '../../../../types/cart';
import { ItemName } from './ItemName';
import { convertToGrams } from '../../../utils/barcodeParser';
import { hasDeepCustomizableComponents, useResolvedComponents } from '../../../hooks/useResolvedComponents';
import { useAllProducts } from '../../../hooks/useAllProducts';
import { buildAddComponentDto } from '../../../utils/cart';

interface Props {
  item: MenuItemVariantDto;
  isClosed: boolean;
}

const Item: FC<Props> = ({ item, isClosed }) => {
  const { addMenuItem, setGiftCardPhoneNumber, giftCardPhoneNumber } = useCartStore(
    (x) => ({
      addMenuItem: x.addMenuItem,
      giftCardPhoneNumber: x.giftCardPhoneNumber,
      setGiftCardPhoneNumber: x.setGiftCardPhoneNumber,
    }),
    shallow
  );

  const quantity = useStandaloneOrderLineQuantity(item.variantLinkId);

  const [isOpenModal, setOpenModal] = useState(false);
  const { location } = useLocation();
  const { formatMessage } = useIntl();

  // Resolve component products (fixes bug where components weren't found)
  const resolvedComponents = useResolvedComponents(item);
  const allProducts = useAllProducts();

  const [openCustomPriceDialog, setOpenCustomPriceDialog] = useState(false);
  const [openWeightPriceDialog, setOpenWeightPriceDialog] = useState(false);
  const [openGiftCardDialog, setOpenGiftCardDialog] = useState(false);
  const [customPrice, setCustomPrice] = useState<number | undefined>(undefined);
  const isGiftCard = Boolean(item.giftCardId);

  const addItemToCart = useCallback(async () => {
    // Build nested components array if this is a composite product
    const components: AddComponentDto[] | undefined =
      item.isComposite && item.components
        ? item.components.map((c) =>
            buildAddComponentDto(c, allProducts, new Map(), item.menuSectionId, item.sectionProductId)
          )
        : undefined;

    const addItem: AddItemDto = {
      components,
      courseId: item.courseId,
      giftCardId: item.giftCardId,
      itemVariantId: item.variantLinkId,
      menuSectionId: item.menuSectionId,
      name: item.name,
      openPrice: !!customPrice,
      options: [],
      price: customPrice ? customPrice : item.price,
      sectionProductId: item.sectionProductId,
      takeawayPrice: customPrice ? customPrice : item.takeawayPrice,
    };

    await addMenuItem(addItem);
  }, [
    addMenuItem,
    allProducts,
    customPrice,
    item.courseId,
    item.giftCardId,
    item.menuSectionId,
    item.name,
    item.price,
    item.sectionProductId,
    item.takeawayPrice,
    item.variantLinkId,
    item.isComposite,
    item.components,
  ]);

  // Check if any resolved components (at any nesting depth) have customizations
  const hasCustomizableComponents = useMemo(
    () => hasDeepCustomizableComponents(resolvedComponents, allProducts),
    [resolvedComponents, allProducts]
  );

  const handleOnItemPress = useCallback(async () => {
    if (isGiftCard) {
      setOpenGiftCardDialog(true);
      return;
    }

    // Use CompositeItemModal if it's a composite with customizations (parent or components)
    if (item.isComposite && (item.optionGroups?.length > 0 || hasCustomizableComponents)) {
      setOpenModal(true);
      return;
    }

    // Use regular ItemModal for non-composite products with customizations
    if (item.optionGroups?.length > 0) {
      setOpenModal(true);
      return;
    }

    if (item.priceType === 'OPEN_PRICE') {
      setOpenCustomPriceDialog(true);
      return;
    }

    if (item.priceType !== 'PER_ITEM') {
      setOpenWeightPriceDialog(true);
      return;
    }

    await addItemToCart();
  }, [
    addItemToCart,
    isGiftCard,
    item.optionGroups?.length,
    item.priceType,
    item.isComposite,
    hasCustomizableComponents,
  ]);

  const addWeightItemToCart = useCallback(
    (weight: number) => {
      // Convert quantity from product's unit to kg, then to grams for unitQuantity
      // Backend always expects unitQuantity in grams as an integer >= 1
      // But price calculation and display should use product's unit (kg/hg/gram)
      let weightKg: number;
      switch (item.priceType) {
        case 'PER_GR':
        case 'PER_GRAM': {
          weightKg = weight / 1000; // grams -> kg
          break;
        }
        case 'PER_HG': {
          weightKg = weight / 10; // hectograms -> kg
          break;
        }
        case 'PER_KG': {
          weightKg = weight; // kg -> kg
          break;
        }
        default: {
          weightKg = weight;
        } // Fallback
      }

      const unitQuantity = convertToGrams(weightKg);

      const addItem: AddItemDto = {
        courseId: item.courseId,
        giftCardId: item.giftCardId,
        itemVariantId: item.variantLinkId,
        menuSectionId: item.menuSectionId,
        name: item.name,
        openPrice: true,
        options: [],
        price: item.price * weight, // Use weight in product's unit for price calculation
        sectionProductId: item.sectionProductId,
        takeawayPrice: item.takeawayPrice ? item.takeawayPrice * weight : item.price * weight,
        unitQuantity, // Send in grams for backend
      };

      addMenuItem(addItem);
    },
    [
      addMenuItem,
      item.courseId,
      item.giftCardId,
      item.menuSectionId,
      item.name,
      item.price,
      item.priceType,
      item.sectionProductId,
      item.takeawayPrice,
      item.variantLinkId,
    ]
  );

  const onModalClose = useCallback(() => {
    setOpenModal(false);
  }, []);

  const buttonBorderColor = quantity ? 'border-brand-primary' : 'border-brand-white';
  const buttonClass = classNames(
    `bg-brand-white col-span-6 desktop:col-span-4 rounded-lg active:bg-purple-lightest border-4 ${buttonBorderColor}`
  );

  return (
    <>
      <div className={buttonClass}>
        <Button
          isDisabled={isClosed}
          height={84}
          onClick={handleOnItemPress}
          className="rounded px-2 relative"
          styleOverride={{ backgroundColor: item.eposDisplayColor }}
          data-cy="menu-item"
          fullWidth
        >
          <>
            <div className="flex flex-col justify-center text-left break-words h-full w-full gap-1">
              <ItemName item={item} />
              <span className="text-left text-xs text-brand-black">
                {isGiftCard
                  ? formatMessage({ id: 'items.giftCard' })
                  : formatPriceWithCurrency(item.price, item.takeawayPrice, item.priceType, location.currency)}
              </span>
            </div>
            {quantity > 0 && (
              <span className="bg-brand-primary text-brand-white text-xs text-center w-5 h-5 p-1 rounded-tl absolute bottom-0 right-0">
                {quantity}
              </span>
            )}
          </>
        </Button>
      </div>
      {isOpenModal &&
        (item.isComposite && (item.optionGroups?.length > 0 || hasCustomizableComponents) ? (
          <CompositeItemModal
            isOpen={isOpenModal}
            item={item}
            componentProducts={resolvedComponents}
            onModalClose={onModalClose}
          />
        ) : (
          <ItemModal isOpen={isOpenModal} item={item} onModalClose={onModalClose} />
        ))}
      {openCustomPriceDialog && (
        <CustomPriceDialog
          isOpen={openCustomPriceDialog}
          aria-label="Open custom price dialog"
          item={item}
          customPrice={customPrice}
          setCustomPrice={setCustomPrice}
          setOpenCustomPriceDialog={setOpenCustomPriceDialog}
          addItemToCart={addItemToCart}
        />
      )}

      {openWeightPriceDialog && (
        <WeightPriceDialog
          isOpen={openWeightPriceDialog}
          item={item}
          onClose={() => setOpenWeightPriceDialog(false)}
          addItemToCart={addWeightItemToCart}
        />
      )}

      {openGiftCardDialog && (
        <GiftCardDialog
          aria-label="Open gift card dialog"
          item={item}
          isOpen={openGiftCardDialog}
          customPrice={customPrice}
          setCustomPrice={setCustomPrice}
          giftCardPhoneNumber={giftCardPhoneNumber}
          setGiftCardPhoneNumber={setGiftCardPhoneNumber}
          setOpenGiftCardDialog={setOpenGiftCardDialog}
          addItemToCart={addItemToCart}
        />
      )}
    </>
  );
};

export default React.memo(Item);
